import Input from '@amzn/meridian/input';
import Row from '@amzn/meridian/row';
import Select, { SelectOption } from '@amzn/meridian/select';
import Text from '@amzn/meridian/text';
import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { getChannelI18nKey, getOwnershipI18nKey } from '../TabHelper';

import { FilterProps } from './FilterHelper';

export const RecentTabFilter: React.FC<FilterProps> = ({
  contacts,
  setFilteredContacts,
  sortDirection,
  sortColumn,
  sourcePage,
  setCurPage,
}: FilterProps): React.ReactElement => {
  const intl = useIntl();
  const optAll = 'ALL';
  const elementAll = (
    <SelectOption
      value={optAll}
      key={optAll}
      label={intl.formatMessage({
        id: 'cs_perfportal_all',
        defaultMessage: 'All',
      })}
    />
  );

  const optsSic = new Array(...new Set(contacts.map(c => c.sic ?? ''))).sort();
  const optsChannel = new Array(
    ...new Set(contacts.map(c => c.channel ?? ''))
  ).sort();
  const optsOwner = new Array(
    ...new Set(contacts.map(c => c.ownership ?? ''))
  ).sort();

  const [selectedSic, setSelectedSic] = useState(optAll);
  const [selectedChannel, setSelectedChannel] = useState(optAll);
  const [selectedOwner, setSelectedOwner] = useState(optAll);
  const [selectedContactId, setSelectedContactId] = useState('');

  useEffect(() => {
    let filtered = new Array(...contacts);
    if (selectedContactId) {
      filtered = filtered.filter(
        c =>
          c.contactID.toLowerCase().indexOf(selectedContactId.toLowerCase()) >
          -1
      );
      if (setCurPage !== undefined) {
        setCurPage(1);
      }
    }

    if (selectedSic !== optAll) {
      filtered = filtered.filter(c => c.sic === selectedSic);
    }

    if (selectedChannel !== optAll) {
      filtered = filtered.filter(c => c.channel === selectedChannel);
    }

    if (selectedOwner !== optAll) {
      filtered = filtered.filter(c => c.ownership === selectedOwner);
    }
    setFilteredContacts(filtered);
  }, [
    selectedSic,
    selectedChannel,
    selectedOwner,
    selectedContactId,
    sortColumn,
    sortDirection,
  ]);

  useEffect(() => {
    if (setCurPage !== undefined) {
      setCurPage(1);
    }
  }, [selectedSic, selectedChannel, selectedOwner, selectedContactId]);

  return (
    <Row width='100%'>
      <Input
        type='text'
        placeholder={intl.formatMessage({
          id: 'cs_perfportal_contact_recom_searchContactId',
          defaultMessage: 'Search for Contact ID',
        })}
        value={selectedContactId}
        onChange={setSelectedContactId}
        width='200px'
        data-csa-c-tag1={sourcePage || ''}
        data-csa-c-tag2='Contact Table'
        data-csa-c-tag3='RECENT'
        data-csa-c-type='element'
        data-csa-c-slot-id='contact-table-cr-recent-tab-search-slot'
        data-csa-c-content-id='contact-table-cr-recent-tab-search-field'
        data-csa-c-selected-value={selectedContactId}
      />
      <Text>
        <FormattedMessage
          id='cs_perfportal_filterBy'
          defaultMessage='Filter by'
        />
      </Text>
      <Select
        value={selectedSic}
        onChange={setSelectedSic}
        label={intl.formatMessage({
          id: 'cs_perfportal_contact_recom_sic',
          defaultMessage: 'SIC',
          description: 'SIC code for recommended contact',
        })}
        width='200px'
        size='xlarge'
      >
        {elementAll}
        {optsSic.map(opt => (
          <SelectOption
            label={opt}
            value={opt}
            key={opt}
            data-csa-c-tag1={sourcePage || ''}
            data-csa-c-tag2='Contact Table'
            data-csa-c-tag3='RECENT'
            data-csa-c-type='select'
            data-csa-c-slot-id='contact-table-cr-recent-tab-sic-filter-slot'
            data-csa-c-content-id='contact-table-cr-recent-tab-sic-filter-select'
            data-csa-c-selected-value={selectedSic}
          />
        ))}
      </Select>
      <Select
        value={selectedChannel}
        onChange={setSelectedChannel}
        label={intl.formatMessage({
          id: 'cs_perfportal_channel',
          defaultMessage: 'Channel',
        })}
        width='200px'
        size='xlarge'
      >
        {elementAll}
        {optsChannel.map(opt => (
          <SelectOption
            label={intl.formatMessage({
              id: getChannelI18nKey(opt),
              defaultMessage: opt,
            })}
            value={opt}
            key={opt}
            data-csa-c-tag1={sourcePage || ''}
            data-csa-c-tag2='Contact Table'
            data-csa-c-tag3='RECENT'
            data-csa-c-type='select'
            data-csa-c-slot-id='contact-table-cr-recent-tab-channel-filter-slot'
            data-csa-c-content-id='contact-table-cr-recent-tab-channel-filter-select'
            data-csa-c-selected-value={selectedChannel}
          />
        ))}
      </Select>

      <Select
        value={selectedOwner}
        onChange={setSelectedOwner}
        label={intl.formatMessage({
          id: 'cs_perfportal_ownership',
          defaultMessage: 'Ownership',
        })}
        width='200px'
        size='xlarge'
      >
        {elementAll}
        {optsOwner.map(opt => (
          <SelectOption
            label={intl.formatMessage({
              id: getOwnershipI18nKey(opt),
              defaultMessage: opt,
            })}
            value={opt}
            key={opt}
            data-csa-c-tag1={sourcePage || ''}
            data-csa-c-tag2='Contact Table'
            data-csa-c-tag3='RECENT'
            data-csa-c-type='select'
            data-csa-c-slot-id='contact-table-cr-recent-tab-owner-filter-slot'
            data-csa-c-content-id='contact-table-cr-recent-tab-owner-filter-select'
            data-csa-c-selected-value={selectedOwner}
          />
        ))}
      </Select>
    </Row>
  );
};
